/**
 * @desc The 'useBreakpoint()' hook is used to get the current
 *       screen breakpoint based on the TailwindCSS config.
 *
 * @usage
 *    import { useBreakpoint } from "@/hooks/useBreakpoint";
 *
 *    const { isAboveSm, isBelowSm, sm } = useBreakpoint("sm", true);
 *    console.log({ isAboveSm, isBelowSm, sm });
 *
 *    const { isAboveMd, isBelowMd, md } = useBreakpoint("md", true);
 *    const { isAboveLg, isBelowLg, lg } = useBreakpoint("lg", false);
 *
 */
import { useCallback, useEffect, useState } from 'react';

import { tailwindConfig } from '../../tailwind-presets';

const breakpoints = tailwindConfig?.theme?.screens;

const parseBreakpointPixels = (value: string | number): number => {
  return Number(String(value).replace(/[^0-9]/g, ''));
};

export function useBreakpoint<K extends string>(breakpointKey: K, watchForChanges?: boolean) {
  type KeyAbove = `isAbove${Capitalize<K>}`;
  type KeyBelow = `isBelow${Capitalize<K>}`;

  const capitalizedKey = breakpointKey[0].toUpperCase() + breakpointKey.substring(1);
  const breakpointValue = breakpoints[breakpointKey as keyof typeof breakpoints];
  const breakpointPx = parseBreakpointPixels(breakpointValue);

  const [breakpointMatchers, setBreakpointMatchers] = useState<
    Record<K, number> & Record<KeyAbove | KeyBelow, boolean>
  >(
    () =>
      ({
        [breakpointKey]: breakpointPx,
        [`isAbove${capitalizedKey}`]: false,
        [`isBelow${capitalizedKey}`]: false,
      }) as Record<K, number> & Record<KeyAbove | KeyBelow, boolean>
  );

  const updateBreakpointStatus = useCallback(
    (matches: boolean) => {
      setBreakpointMatchers({
        [breakpointKey]: breakpointPx,
        [`isAbove${capitalizedKey}`]: matches,
        [`isBelow${capitalizedKey}`]: !matches,
      } as Record<K, number> & Record<KeyAbove | KeyBelow, boolean>);
    },
    [breakpointKey, breakpointPx, capitalizedKey]
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(`(min-width: ${breakpointValue})`);

    updateBreakpointStatus(mediaQuery.matches);

    if (!watchForChanges) return;

    const handleChange = (event: MediaQueryListEvent) => {
      updateBreakpointStatus(event.matches);
    };

    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [breakpointValue, updateBreakpointStatus, watchForChanges]);

  return breakpointMatchers;
}

export const useWatchScreenSize = () => {
  const [currentBreakpoint, setCurrentBreakpoint] = useState<keyof typeof breakpoints>(() => {
    if (typeof window === 'undefined') return 'xl';

    const width = window.innerWidth;
    const higherItems = Object.entries(breakpoints).filter(([, value]) => {
      const breakpointPx = parseBreakpointPixels(value);
      return width >= breakpointPx;
    });
    const matched = [...higherItems].pop();
    return matched ? (matched[0] as keyof typeof breakpoints) : 'xxs';
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQueries = Object.entries(breakpoints).map(([key, value]) => ({
      key: key as keyof typeof breakpoints,
      query: window.matchMedia(`(min-width: ${value})`),
    }));

    const updateBreakpointStatus = () => {
      const matchingBreakpoints = mediaQueries.filter(({ query }) => query.matches);
      const highestMatch = matchingBreakpoints[matchingBreakpoints.length - 1];

      const newBreakpoint = highestMatch ? highestMatch.key : 'xxs';

      setCurrentBreakpoint((current) => {
        return current !== newBreakpoint ? newBreakpoint : current;
      });
    };

    updateBreakpointStatus();

    const handleChange = () => updateBreakpointStatus();

    mediaQueries.forEach(({ query }) => {
      query.addEventListener('change', handleChange);
    });

    return () => {
      mediaQueries.forEach(({ query }) => {
        query.removeEventListener('change', handleChange);
      });
    };
  }, []);

  return currentBreakpoint;
};

export type BreakpointMatches =
  | keyof typeof breakpoints
  | boolean
  | 'max-sm'
  | 'max-md'
  | 'max-lg'
  | 'max-xl';
export const matchesBreakpoint = (
  breakpoint: BreakpointMatches,
  screenSize: keyof typeof breakpoints
) => {
  if (typeof breakpoint === 'boolean') {
    return breakpoint;
  }

  const screenSizePx = parseBreakpointPixels(breakpoints[screenSize]);

  if (breakpoint.includes('max-')) {
    const maxBreakpoint = breakpoint.replace('max-', '') as keyof typeof breakpoints;
    const maxBreakpointPx = parseBreakpointPixels(breakpoints[maxBreakpoint]);
    return screenSizePx <= maxBreakpointPx;
  }

  const breakpointPx = parseBreakpointPixels(breakpoints[breakpoint as keyof typeof breakpoints]);
  return screenSizePx >= breakpointPx;
};

export const isAboveBreakpoint = (
  currentBreakpoint: keyof typeof breakpoints,
  screenBreakpoint: keyof typeof breakpoints
) => {
  return (
    parseBreakpointPixels(breakpoints[currentBreakpoint]) >=
    parseBreakpointPixels(breakpoints[screenBreakpoint])
  );
};

export const isBelowBreakpoint = (
  currentBreakpoint: keyof typeof breakpoints,
  screenBreakpoint: keyof typeof breakpoints
) => {
  return !isAboveBreakpoint(currentBreakpoint, screenBreakpoint);
};
